import { CurrencyPipe } from '@angular/common';
import { DEFAULT_CURRENCY_CODE, Inject, LOCALE_ID, Pipe, PipeTransform } from '@angular/core';
import { AdminService } from '@app/services/admin.service';
import { normalizeNumberOfDecimals } from '@app/utils/functions/decimals';

/**
 * CI-21979 — Pipe central de formato monetario.
 *
 * Cuando CMS entrega una configuración válida (`numberOfDecimalsBE` = 0, 1 o 2),
 * aplica exactamente esa cantidad de decimales (mínimo y máximo) sin alterar el
 * valor. Cuando la configuración está ausente o es inválida NO fuerza ningún
 * formato: reproduce el formato previo de cada pantalla mediante `fallbackDigits`
 * (el `digitsInfo` original del template; si se omite, equivale al comportamiento
 * por defecto de `| currency`). Delega en {@link CurrencyPipe} para conservar el
 * locale, el currency code y el símbolo.
 *
 * - Solo presentación: no muta el valor de entrada ni objetos.
 * - Seguro ante null / undefined / valores no numéricos (devuelve null).
 * - Sin valor por defecto de decimales.
 */
@Pipe({
  name: 'amCurrency',
  standalone: false,
})
export class AmCurrencyPipe implements PipeTransform {
  private readonly currencyPipe: CurrencyPipe;

  constructor(
    @Inject(LOCALE_ID) private readonly localeId: string,
    @Inject(DEFAULT_CURRENCY_CODE) private readonly defaultCurrencyCode: string,
    private readonly adminService: AdminService
  ) {
    this.currencyPipe = new CurrencyPipe(this.localeId, this.defaultCurrencyCode);
  }

  /**
   * @param value monto a formatear.
   * @param currencyCode código de moneda (igual que CurrencyPipe).
   * @param display símbolo / código (igual que CurrencyPipe).
   * @param fallbackDigits `digitsInfo` original del template, usado SOLO cuando no
   *        hay configuración válida de decimales (preserva el formato previo).
   * @param locale locale opcional.
   */
  transform(
    value: number | string | null | undefined,
    currencyCode?: string,
    display: 'code' | 'symbol' | 'symbol-narrow' | string | boolean = 'symbol',
    fallbackDigits?: string,
    locale?: string
  ): string | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    // No se modifica el valor: se usa una copia numérica solo para formatear.
    const numericValue = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(numericValue)) {
      return null;
    }

    // Configuración válida (0/1/2) → se aplica; ausente/inválida → fallback del
    // template (sin default forzado).
    const decimals = normalizeNumberOfDecimals(this.adminService?.admin?.numberOfDecimalsBE);
    const digitsInfo = decimals !== undefined ? `1.${decimals}-${decimals}` : fallbackDigits;

    return this.currencyPipe.transform(numericValue, currencyCode, display, digitsInfo, locale);
  }
}
